import React, { useState, useRef } from 'react';
import { 
  Briefcase, 
  Globe, 
  Building2, 
  Clock, 
  MapPin, 
  CheckCircle2, 
  ArrowRight, 
  Search, 
  Upload, 
  FileText, 
  X, 
  Sparkles, 
  ShieldCheck, 
  Loader2, 
  ChevronRight,
  SlidersHorizontal,
  DollarSign,
  Laptop,
  Users
} from 'lucide-react';
import { jobPostingsData, employeePerks } from '../data/careersData';
import { JobPosting, WorkModeType } from '../types';
import { sendJobApplication } from '../services/emailService';

interface CareersPageProps {
  onNavigate: (path: string) => void;
  onOpenConsultation?: () => void;
}

export const CareersPage: React.FC<CareersPageProps> = ({ onNavigate }) => {
  // Filters state
  const [selectedWorkMode, setSelectedWorkMode] = useState<string>('All');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Selected JD for Modal view
  const [activeJDModal, setActiveJDModal] = useState<JobPosting | null>(null);

  // Application Form state
  const [applicationForm, setApplicationForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    jobId: jobPostingsData[0].id,
    jobTitle: jobPostingsData[0].title,
    workPreference: 'Remote (WFH)' as WorkModeType,
    experienceYears: '3 - 5 Years',
    currentLocation: '',
    portfolioUrl: '',
    resumeFileName: '',
    resumeLink: '',
    coverNote: '',
    noticePeriod: 'Immediate Joiner (0 - 15 Days)',
  });

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedAppId, setSubmittedAppId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Filter logic
  const filteredJobs = jobPostingsData.filter((job) => {
    // Work mode filter
    if (selectedWorkMode !== 'All') {
      if (selectedWorkMode === 'Remote' && !job.workType.includes('Remote')) return false;
      if (selectedWorkMode === 'WFO' && !job.workType.includes('Office')) return false;
      if (selectedWorkMode === 'Hybrid' && !job.workType.includes('Hybrid')) return false;
    }
    // Department filter
    if (selectedDepartment !== 'All' && job.department !== selectedDepartment) {
      return false;
    }
    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const match = 
        job.title.toLowerCase().includes(q) ||
        job.department.toLowerCase().includes(q) ||
        job.shortSummary.toLowerCase().includes(q) ||
        job.workType.toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  const handleApplyClick = (job: JobPosting) => {
    setApplicationForm((prev) => ({
      ...prev,
      jobId: job.id,
      jobTitle: job.title,
      workPreference: job.workType,
    }));
    setActiveJDModal(null);
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadedFile(file);
      setApplicationForm(prev => ({
        ...prev,
        resumeFileName: `${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`
      }));
    }
  };

  const handleSubmitApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applicationForm.fullName.trim() || !applicationForm.email.trim() || !applicationForm.phone.trim()) {
      setFormError('Please fill in your full name, email address, and phone number.');
      return;
    }
    if (!uploadedFile && !applicationForm.resumeLink.trim()) {
      setFormError('Please upload your resume file (PDF/Doc) or provide a cloud link (Google Drive/Dropbox).');
      return;
    }

    setFormError(null);
    setIsSubmitting(true);

    try {
      const res = await sendJobApplication({
        fullName: applicationForm.fullName,
        email: applicationForm.email,
        phone: applicationForm.phone,
        jobId: applicationForm.jobId,
        jobTitle: applicationForm.jobTitle,
        workPreference: applicationForm.workPreference,
        experienceYears: applicationForm.experienceYears,
        currentLocation: applicationForm.currentLocation,
        portfolioUrl: applicationForm.portfolioUrl,
        resumeFileName: applicationForm.resumeFileName,
        resumeLink: applicationForm.resumeLink,
        coverNote: applicationForm.coverNote,
        noticePeriod: applicationForm.noticePeriod,
        resumeFile: uploadedFile,
      });

      setIsSubmitting(false);
      if (res.success) {
        const randomId = res.leadId || 'EBON-CAREER-' + Math.floor(10000 + Math.random() * 90000);
        setSubmittedAppId(randomId);
      } else {
        setFormError(res.error || 'Failed to submit application. Please send your resume to info@ebonmedia.com');
      }
    } catch (err: any) {
      setIsSubmitting(false);
      setFormError('Application submission error. Please email your CV to info@ebonmedia.com directly.');
    }
  };

  const resetForm = () => {
    setSubmittedAppId(null);
    setUploadedFile(null);
    setApplicationForm({
      fullName: '',
      email: '',
      phone: '',
      jobId: jobPostingsData[0].id,
      jobTitle: jobPostingsData[0].title,
      workPreference: 'Remote (WFH)',
      experienceYears: '3 - 5 Years',
      currentLocation: '',
      portfolioUrl: '',
      resumeFileName: '',
      resumeLink: '',
      coverNote: '',
      noticePeriod: 'Immediate Joiner (0 - 15 Days)',
    });
  };

  const getWorkBadgeColor = (type: WorkModeType) => {
    if (type.includes('Remote')) {
      return 'bg-[#159DE8]/10 text-[#159DE8] border-[#159DE8]/40';
    }
    if (type.includes('Office')) {
      return 'bg-[#00C853]/10 text-[#00C853] border-[#00C853]/40';
    }
    return 'bg-[#FF851B]/10 text-[#FF851B] border-[#FF851B]/40';
  };

  return (
    <div className="bg-[#071426] text-[#D5DFEC] py-12 space-y-20 min-h-screen">
      
      {/* ========================================================================= */}
      {/* 1. HERO SECTION */}
      {/* ========================================================================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-5">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0B2342] border border-[#1b2f4a] text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
            <Briefcase size={14} className="text-[#159DE8]" />
            <span>WE ARE ACTIVELY HIRING • REMOTE & WFO ROLES</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white font-display tracking-tight leading-tight">
            Build Cutting-Edge Tech with EBON Media Solutions
          </h1>

          <p className="text-base sm:text-lg text-[#D5DFEC] leading-relaxed">
            Join a forward-thinking engineering and digital growth studio. Whether you thrive working <strong>100% remotely from home (WFH)</strong> or prefer the vibrant collaboration of <strong>Work From Office (WFO)</strong>, we have open vacancies waiting for your expertise.
          </p>

          {/* Quick Counter Pills */}
          <div className="flex flex-wrap items-center justify-center gap-4 pt-2 text-xs">
            <div className="px-4 py-2 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#159DE8] animate-pulse" />
              <span className="text-white font-bold">{jobPostingsData.length} Open Vacancies</span>
            </div>
            <div className="px-4 py-2 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] flex items-center gap-2">
              <Globe size={14} className="text-[#159DE8]" />
              <span className="text-[#D5DFEC]">Remote (WFH) & Office (WFO)</span>
            </div>
            <div className="px-4 py-2 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] flex items-center gap-2">
              <Clock size={14} className="text-[#00C853]" />
              <span className="text-[#D5DFEC]">Quick Review (&lt; 48 Hours)</span>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 2. FILTERS & SEARCH TOOLBAR */}
      {/* ========================================================================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-6 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] shadow-card-dark space-y-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            
            {/* Work Mode Toggle Pills */}
            <div className="space-y-1.5">
              <span className="text-[11px] font-mono text-[#8FA0B8] uppercase tracking-wider block">
                Work Mode Preference:
              </span>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'All', label: 'All Modes' },
                  { id: 'Remote', label: '🌐 Remote Work (WFH)' },
                  { id: 'WFO', label: '🏢 Work From Office (WFO)' },
                  { id: 'Hybrid', label: '⚖️ Hybrid' },
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setSelectedWorkMode(m.id)}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                      selectedWorkMode === m.id
                        ? 'bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white border-transparent shadow-sm'
                        : 'bg-[#071426] text-[#8FA0B8] border-[#1b2f4a] hover:text-white hover:border-[#159DE8]'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Department Selector */}
            <div className="space-y-1.5">
              <span className="text-[11px] font-mono text-[#8FA0B8] uppercase tracking-wider block">
                Department:
              </span>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="px-3.5 py-2 bg-[#071426] border border-[#1b2f4a] rounded-lg text-white text-xs focus:outline-none focus:border-[#159DE8]"
              >
                <option value="All">All Departments</option>
                <option value="Engineering">Engineering</option>
                <option value="Mobile Apps">Mobile Apps</option>
                <option value="Digital Marketing">Digital Marketing</option>
                <option value="Design & Creative">Design & Creative</option>
                <option value="Sales & BD">Sales & BD</option>
              </select>
            </div>

            {/* Search Input */}
            <div className="space-y-1.5 lg:w-72">
              <span className="text-[11px] font-mono text-[#8FA0B8] uppercase tracking-wider block">
                Search Vacancies:
              </span>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8FA0B8]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="e.g. React, SEO, Remote..."
                  className="w-full pl-9 pr-3.5 py-2 bg-[#071426] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/50 text-xs focus:outline-none focus:border-[#159DE8]"
                />
              </div>
            </div>

          </div>

          <div className="flex items-center justify-between pt-2 border-t border-[#1b2f4a]/60 text-xs text-[#8FA0B8]">
            <span>Showing <strong className="text-white">{filteredJobs.length}</strong> available openings</span>
            {(selectedWorkMode !== 'All' || selectedDepartment !== 'All' || searchQuery) && (
              <button
                onClick={() => {
                  setSelectedWorkMode('All');
                  setSelectedDepartment('All');
                  setSearchQuery('');
                }}
                className="text-[#159DE8] hover:underline"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 3. VACANCIES & JOB DESCRIPTIONS GRID */}
      {/* ========================================================================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div>
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF851B] block mb-1">
            OPEN VACANCIES
          </span>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
            Explore Open Roles & Job Descriptions (JDs)
          </h2>
          <p className="text-xs sm:text-sm text-[#8FA0B8] mt-1">
            Click on any opening to inspect the full JD, required qualifications, and benefits.
          </p>
        </div>

        {filteredJobs.length === 0 ? (
          <div className="p-12 text-center rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] space-y-3">
            <Briefcase size={32} className="mx-auto text-[#8FA0B8]" />
            <h3 className="text-lg font-bold text-white">No Vacancies Matched Your Filters</h3>
            <p className="text-xs text-[#8FA0B8] max-w-md mx-auto">
              We frequently add new engineering, mobile app, and marketing roles. You can submit a general resume below for future consideration!
            </p>
            <button
              onClick={() => {
                setSelectedWorkMode('All');
                setSelectedDepartment('All');
                setSearchQuery('');
              }}
              className="px-5 py-2 rounded-full bg-[#0B2342] text-xs font-bold text-white border border-[#1b2f4a] hover:border-[#FF851B]"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredJobs.map((job) => (
              <div
                key={job.id}
                className="p-7 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] hover:border-[#159DE8]/60 transition-all duration-300 shadow-card-dark flex flex-col justify-between group"
              >
                <div className="space-y-4">
                  {/* Top Badges */}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border flex items-center gap-1.5 ${getWorkBadgeColor(job.workType)}`}>
                      {job.workType.includes('Remote') ? <Globe size={12} /> : <Building2 size={12} />}
                      <span>{job.workType}</span>
                    </span>

                    <span className="text-[11px] font-mono text-[#8FA0B8] bg-[#071426] px-2 py-0.5 rounded border border-[#1b2f4a]">
                      {job.department}
                    </span>
                  </div>

                  {/* Job Title */}
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white font-display group-hover:text-[#FF851B] transition-colors">
                      {job.title}
                    </h3>
                    <p className="text-xs text-[#8FA0B8] mt-2 leading-relaxed">
                      {job.shortSummary}
                    </p>
                  </div>

                  {/* Meta Details: Exp, Location, Openings */}
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[#1b2f4a]/70 text-xs">
                    <div className="flex items-center gap-1.5 text-[#D5DFEC]">
                      <Clock size={13} className="text-[#159DE8] shrink-0" />
                      <span>Exp: {job.experience}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[#D5DFEC]">
                      <Users size={13} className="text-[#00C853] shrink-0" />
                      <span>{job.openings} Openings</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[#8FA0B8] col-span-2">
                      <MapPin size={13} className="text-[#FF851B] shrink-0" />
                      <span className="truncate">{job.location}</span>
                    </div>
                  </div>
                </div>

                {/* Card Actions */}
                <div className="pt-6 mt-6 border-t border-[#1b2f4a] flex items-center justify-between gap-3">
                  <button
                    onClick={() => setActiveJDModal(job)}
                    className="text-xs font-bold text-[#159DE8] hover:text-[#FF851B] transition-colors flex items-center gap-1"
                  >
                    <span>View Full JD</span>
                    <ChevronRight size={13} />
                  </button>

                  <button
                    onClick={() => handleApplyClick(job)}
                    className="px-5 py-2 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-xs font-bold hover:shadow-glow-orange transition-all flex items-center gap-1.5"
                  >
                    <span>Apply for this Role</span>
                    <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ========================================================================= */}
      {/* 4. RESUME SUBMISSION APPLICATION FORM */}
      {/* ========================================================================= */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" ref={formRef} id="apply-form">
        <div className="p-8 sm:p-10 rounded-3xl bg-[#0c1a2d] border border-[#1b2f4a] shadow-2xl relative overflow-hidden">
          
          <div className="mb-8 space-y-2">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
              <Upload size={14} />
              <span>CANDIDATE APPLICATION PORTAL</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
              Submit Your Resume / Application
            </h2>
            <p className="text-xs sm:text-sm text-[#8FA0B8]">
              Apply directly for a selected Job Description (JD) or submit your resume for Remote (WFH) or Work From Office (WFO) vacancies.
            </p>
          </div>

          {submittedAppId ? (
            <div className="py-12 text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-green-500/20 text-[#00C853] rounded-full flex items-center justify-center border border-green-500/40">
                <CheckCircle2 size={36} />
              </div>
              <h3 className="text-2xl font-bold text-white font-display">Resume Successfully Submitted!</h3>
              <p className="text-sm text-[#D5DFEC] max-w-md mx-auto leading-relaxed">
                Thank you, <strong className="text-white">{applicationForm.fullName}</strong>. Your application has been logged into our talent database with Reference ID:
              </p>
              
              <div className="inline-block px-4 py-2 rounded-xl bg-[#050A13] border border-[#159DE8] text-sm font-mono font-bold text-[#159DE8]">
                {submittedAppId}
              </div>

              <div className="p-4 bg-[#071426] border border-[#1b2f4a] rounded-xl max-w-md mx-auto text-left text-xs space-y-1.5 text-[#D5DFEC]">
                <div><span className="text-[#8FA0B8]">Role Applied:</span> <strong className="text-white">{applicationForm.jobTitle}</strong></div>
                <div><span className="text-[#8FA0B8]">Work Mode Preference:</span> <strong className="text-[#00C853]">{applicationForm.workPreference}</strong></div>
                <div><span className="text-[#8FA0B8]">Notice Period:</span> {applicationForm.noticePeriod}</div>
                <div><span className="text-[#8FA0B8]">Review Timeline:</span> Our recruitment team typically reviews submissions within 24 to 48 business hours.</div>
              </div>

              <button
                onClick={resetForm}
                className="mt-6 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-xs font-bold hover:shadow-glow-orange transition-all"
              >
                Submit Another Application
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmitApplication} className="space-y-5">
              {formError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs">
                  {formError}
                </div>
              )}

              {/* Row 1: Name & Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={applicationForm.fullName}
                    onChange={(e) => setApplicationForm({ ...applicationForm, fullName: e.target.value })}
                    placeholder="e.g. Rahul Sharma"
                    className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={applicationForm.email}
                    onChange={(e) => setApplicationForm({ ...applicationForm, email: e.target.value })}
                    placeholder="rahul@example.com"
                    className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-sm"
                  />
                </div>
              </div>

              {/* Row 2: Phone & Current Location */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Phone Number (with WhatsApp) *</label>
                  <input
                    type="tel"
                    required
                    value={applicationForm.phone}
                    onChange={(e) => setApplicationForm({ ...applicationForm, phone: e.target.value })}
                    placeholder="+91 98765 43210"
                    className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Current City / Location</label>
                  <input
                    type="text"
                    value={applicationForm.currentLocation}
                    onChange={(e) => setApplicationForm({ ...applicationForm, currentLocation: e.target.value })}
                    placeholder="e.g. Bangalore, Mumbai, Remote India"
                    className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-sm"
                  />
                </div>
              </div>

              {/* Row 3: Role / JD Selection */}
              <div>
                <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Applying for Position (JD) *</label>
                <select
                  value={applicationForm.jobId}
                  onChange={(e) => {
                    const sel = jobPostingsData.find(j => j.id === e.target.value);
                    setApplicationForm({
                      ...applicationForm,
                      jobId: e.target.value,
                      jobTitle: sel ? sel.title : 'General Application',
                      workPreference: sel ? sel.workType : applicationForm.workPreference
                    });
                  }}
                  className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white text-sm focus:outline-none focus:border-[#159DE8]"
                >
                  {jobPostingsData.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.title} — [{job.workType}]
                    </option>
                  ))}
                  <option value="general-application">General Application / Future Engineering Openings</option>
                </select>
              </div>

              {/* Row 4: Work Mode Preference Radio Selector */}
              <div>
                <label className="block text-xs font-medium text-[#8FA0B8] mb-2">
                  Your Work Mode Preference *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { type: 'Remote (WFH)' as WorkModeType, label: '🌐 Remote (WFH)', desc: '100% Home Based' },
                    { type: 'Work From Office (WFO)' as WorkModeType, label: '🏢 Work From Office (WFO)', desc: 'On-site collaborative hub' },
                    { type: 'Hybrid' as WorkModeType, label: '⚖️ Hybrid / Flexible', desc: 'Mix of office & remote' },
                  ].map((item) => (
                    <button
                      key={item.type}
                      type="button"
                      onClick={() => setApplicationForm({ ...applicationForm, workPreference: item.type })}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        applicationForm.workPreference === item.type
                          ? 'bg-[#0B2342] border-[#159DE8] shadow-md ring-1 ring-[#159DE8]'
                          : 'bg-[#050A13] border-[#1b2f4a] hover:border-[#159DE8]/50'
                      }`}
                    >
                      <div className="text-xs font-bold text-white">{item.label}</div>
                      <div className="text-[10px] text-[#8FA0B8] mt-0.5">{item.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Row 5: Experience & Notice Period */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Total Relevant Experience</label>
                  <select
                    value={applicationForm.experienceYears}
                    onChange={(e) => setApplicationForm({ ...applicationForm, experienceYears: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white text-sm focus:outline-none focus:border-[#159DE8]"
                  >
                    <option value="0 - 1 Year (Entry/Fresher)">0 - 1 Year (Entry/Fresher)</option>
                    <option value="1 - 3 Years (Junior/Mid)">1 - 3 Years (Junior/Mid)</option>
                    <option value="3 - 5 Years (Mid-Senior)">3 - 5 Years (Mid-Senior)</option>
                    <option value="5 - 8 Years (Senior)">5 - 8 Years (Senior)</option>
                    <option value="8+ Years (Lead/Architect)">8+ Years (Lead/Architect)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Notice Period / Availability</label>
                  <select
                    value={applicationForm.noticePeriod}
                    onChange={(e) => setApplicationForm({ ...applicationForm, noticePeriod: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white text-sm focus:outline-none focus:border-[#159DE8]"
                  >
                    <option value="Immediate Joiner (0 - 15 Days)">Immediate Joiner (0 - 15 Days)</option>
                    <option value="30 Days Notice">30 Days Notice</option>
                    <option value="60 Days Notice">60 Days Notice</option>
                    <option value="Currently Serving Notice Period">Currently Serving Notice Period</option>
                  </select>
                </div>
              </div>

              {/* Row 6: Portfolio / GitHub / LinkedIn */}
              <div>
                <label className="block text-xs font-medium text-[#8FA0B8] mb-1">
                  Portfolio / GitHub / LinkedIn Profile URL
                </label>
                <input
                  type="url"
                  value={applicationForm.portfolioUrl}
                  onChange={(e) => setApplicationForm({ ...applicationForm, portfolioUrl: e.target.value })}
                  placeholder="https://linkedin.com/in/... or github.com/..."
                  className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-sm"
                />
              </div>

              {/* Row 7: Resume File Upload Box & Cloud Link */}
              <div className="space-y-3">
                <label className="block text-xs font-medium text-[#8FA0B8]">
                  Submit Your Resume (PDF, DOC, DOCX up to 10MB) *
                </label>

                {/* File Upload Zone */}
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="p-6 rounded-2xl bg-[#050A13] border-2 border-dashed border-[#1b2f4a] hover:border-[#159DE8] transition-all cursor-pointer text-center space-y-2 group"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  
                  {uploadedFile ? (
                    <div className="flex items-center justify-center gap-2 text-sm text-[#00C853] font-semibold">
                      <FileText size={20} />
                      <span>{applicationForm.resumeFileName}</span>
                      <span className="text-xs text-[#8FA0B8] ml-2">(Click to replace)</span>
                    </div>
                  ) : (
                    <>
                      <div className="w-12 h-12 mx-auto rounded-full bg-[#0B2342] text-[#159DE8] flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Upload size={20} />
                      </div>
                      <div className="text-xs sm:text-sm font-semibold text-white">
                        Click to browse & upload resume from your device
                      </div>
                      <div className="text-[11px] text-[#8FA0B8]">
                        Supports PDF, Word (.doc, .docx) • Max 10MB
                      </div>
                    </>
                  )}
                </div>

                {/* Alternative: Cloud Link */}
                <div>
                  <span className="text-[11px] text-[#8FA0B8] block mb-1">
                    Or paste Google Drive / Dropbox / Notion Resume Link:
                  </span>
                  <input
                    type="url"
                    value={applicationForm.resumeLink}
                    onChange={(e) => setApplicationForm({ ...applicationForm, resumeLink: e.target.value })}
                    placeholder="https://drive.google.com/file/d/..."
                    className="w-full px-3.5 py-2 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-xs"
                  />
                </div>
              </div>

              {/* Row 8: Brief Cover Note */}
              <div>
                <label className="block text-xs font-medium text-[#8FA0B8] mb-1">
                  Brief Note / Highlight Your Key Skills & Projects
                </label>
                <textarea
                  rows={3}
                  value={applicationForm.coverNote}
                  onChange={(e) => setApplicationForm({ ...applicationForm, coverNote: e.target.value })}
                  placeholder="Share a short summary of your standout work, tech stacks, or why you'd like to work with EBON Media..."
                  className="w-full px-3.5 py-2 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-sm resize-none"
                />
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white font-bold text-sm hover:shadow-glow-orange hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-60 shadow-lg"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>Submitting Your Resume & Application...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Application for {applicationForm.jobTitle.split('(')[0]}</span>
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
                <p className="text-[11px] text-center text-[#8FA0B8] mt-2 flex items-center justify-center gap-1.5">
                  <ShieldCheck size={13} className="text-[#00C853]" />
                  <span>Candidate privacy guaranteed. Your resume is kept strictly confidential.</span>
                </p>
              </div>
            </form>
          )}

        </div>
      </section>

      {/* ========================================================================= */}
      {/* 5. WHY WORK AT EBON MEDIA SOLUTIONS */}
      {/* ========================================================================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-12 rounded-3xl bg-[#050A13] border border-[#1b2f4a] space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF851B]">
              CULTURE & BENEFITS
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
              Why Engineers & Marketers Choose EBON Media
            </h2>
            <p className="text-xs sm:text-sm text-[#8FA0B8]">
              We build great software by trusting great people and eliminating bureaucracy.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {employeePerks.map((perk, idx) => (
              <div key={idx} className="p-6 rounded-2xl bg-[#071426] border border-[#1b2f4a] space-y-3">
                <span className="text-xs font-mono font-bold text-[#159DE8]">0{idx + 1}</span>
                <h3 className="text-base font-bold text-white">{perk.title}</h3>
                <p className="text-xs text-[#8FA0B8] leading-relaxed">{perk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 6. FULL JOB DESCRIPTION (JD) MODAL */}
      {/* ========================================================================= */}
      {activeJDModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#050A13]/85 backdrop-blur-md animate-fade-in"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full max-w-3xl bg-[#071426] border border-[#1b2f4a] rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-[#1b2f4a] flex items-center justify-between bg-[#0B2342]/50">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${getWorkBadgeColor(activeJDModal.workType)}`}>
                    {activeJDModal.workType}
                  </span>
                  <span className="text-[11px] font-mono text-[#8FA0B8]">
                    {activeJDModal.department}
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white font-display">
                  {activeJDModal.title}
                </h3>
              </div>
              <button
                onClick={() => setActiveJDModal(null)}
                className="p-2 text-[#8FA0B8] hover:text-white hover:bg-white/10 rounded-full transition-colors"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 sm:p-8 overflow-y-auto space-y-6 text-xs sm:text-sm text-[#D5DFEC]">
              
              {/* Meta bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3.5 rounded-xl bg-[#050A13] border border-[#1b2f4a] text-xs">
                <div>
                  <span className="text-[#8FA0B8] block text-[10px]">Work Mode:</span>
                  <strong className="text-white">{activeJDModal.workType}</strong>
                </div>
                <div>
                  <span className="text-[#8FA0B8] block text-[10px]">Experience:</span>
                  <strong className="text-white">{activeJDModal.experience}</strong>
                </div>
                <div>
                  <span className="text-[#8FA0B8] block text-[10px]">Openings:</span>
                  <strong className="text-white">{activeJDModal.openings} Positions</strong>
                </div>
                <div>
                  <span className="text-[#8FA0B8] block text-[10px]">Compensation:</span>
                  <strong className="text-[#00C853]">{activeJDModal.salaryRange}</strong>
                </div>
              </div>

              {/* Role Overview */}
              <div className="space-y-2">
                <h4 className="text-sm font-bold text-white uppercase tracking-wider text-[#FF851B]">
                  Role Overview
                </h4>
                <p className="leading-relaxed text-[#D5DFEC]">
                  {activeJDModal.fullJD.overview}
                </p>
              </div>

              {/* Responsibilities */}
              <div className="space-y-2">
                <h4 className="text-sm font-bold text-white uppercase tracking-wider text-[#159DE8]">
                  Key Responsibilities
                </h4>
                <ul className="space-y-2">
                  {activeJDModal.fullJD.responsibilities.map((resp, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <CheckCircle2 size={14} className="text-[#159DE8] shrink-0 mt-1" />
                      <span>{resp}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Requirements */}
              <div className="space-y-2">
                <h4 className="text-sm font-bold text-white uppercase tracking-wider text-[#00C853]">
                  Required Qualifications & Skills
                </h4>
                <ul className="space-y-2">
                  {activeJDModal.fullJD.requirements.map((req, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <CheckCircle2 size={14} className="text-[#00C853] shrink-0 mt-1" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Nice to have */}
              {activeJDModal.fullJD.niceToHave.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-bold text-white uppercase tracking-wider text-[#FF851B]">
                    Nice to Have
                  </h4>
                  <ul className="space-y-2 text-[#8FA0B8]">
                    {activeJDModal.fullJD.niceToHave.map((nth, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF851B] mt-1.5 shrink-0" />
                        <span>{nth}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Perks */}
              <div className="space-y-2 p-4 rounded-xl bg-[#050A13] border border-[#1b2f4a]">
                <h4 className="text-sm font-bold text-white">What We Offer:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {activeJDModal.fullJD.perksAndBenefits.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 text-[#D5DFEC]">
                      <Sparkles size={13} className="text-[#FF851B] shrink-0" />
                      <span>{p}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-[#1b2f4a] bg-[#050A13] flex items-center justify-between">
              <button
                onClick={() => setActiveJDModal(null)}
                className="text-xs font-semibold text-[#8FA0B8] hover:text-white"
              >
                Close Job Description
              </button>

              <button
                onClick={() => handleApplyClick(activeJDModal)}
                className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-xs font-bold hover:shadow-glow-orange transition-all flex items-center gap-1.5"
              >
                <span>Apply for this Job</span>
                <ArrowRight size={14} />
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};
